import { createListenerMiddleware } from '@reduxjs/toolkit'
import { AnalyticsEvent, type AnalyticsEventProperties } from '@/analytics/schema/events.schema'
import { consumeDictationUsed } from '@/analytics/utils/dictation-analytics'
import { newChatMessageSkillProperties } from '@/analytics/utils/skill-analytics'
import { getTrackEventGlobal } from '@/analytics/utils/analytics-helpers'
import { trackAssistantTableCreated } from '@/analytics/utils/table-analytics'
import { formatRiaDateRange } from '@/analytics/utils/formatRiaDateRange'
import { AgentV2ConversationsActions, AgentV2RunPhase } from '@/agent-v2/conversations/store/slice'
import { AgentV2TranscriptActions } from '@/agent-v2/store/slice'
import { AgentV2ServerEventType } from '@/agent-v2/store/schemas'
import { selectAgentV2ConversationMatterId } from '@/agent-v2/conversations/store/selectors'
// The Research-in-Assistant source controls render in ChatInputBox. Their chat-form state
// is the source of truth for the research payload. This listener only observes it.
import {
  selectAgentConversationFormCaselawDateRange,
  selectAgentConversationFormIncludeUnpublishedCases,
  selectAgentConversationFormResearchEnabled,
  selectAgentConversationFormWebSearchEnabled,
} from '@/agent-v2/chat-form/store/selectors'
import {
  selectAreAllFederalTabSourcesSelected,
  selectHasRelatedFederalContent,
  selectNumberOfCurrentSourcesShownInFederalTab,
  selectSelectedStateNames,
} from '@/agent-v2/chat-form/sources-dialog/store/selectors'
import type { RootState } from '@/store/store'

/**
 * Chat analytics.
 *
 * Observational only: this middleware reacts to actions the chat flow already dispatches and
 * never dispatches back, so the chat is entirely unaware of analytics. `turnSubmitted` is the
 * single submit action behind both composers (AgentV2ChatComposer and MatterOverviewChatInput),
 * so one listener covers every send.
 *
 * `NewChatMessage` waits until the turn is accepted, because that is when the backend turn id
 * exists. The send properties are captured at submit: the skill picker clears on admission, and
 * dictation is consumed when the user sends. The event uses the tracker that was current at send.
 * On the chat page that tracker keeps the route feature and the context-menu matter from that
 * render.
 * The event fires once, from the live run start or from the HTTP recovery that supplies the same
 * id. A send that never starts is dropped.
 */
type NewChatMessageProperties = AnalyticsEventProperties[AnalyticsEvent.NewChatMessage]

type PendingNewChatMessage = {
  conversationId: string
  properties: Omit<NewChatMessageProperties, 'turnId'>
  track: ReturnType<typeof getTrackEventGlobal>
}

const pendingNewChatMessages = new Map<string, PendingNewChatMessage>()

export function clearPendingNewChatMessages() {
  pendingNewChatMessages.clear()
}

// Table ids seen on a live `table.created`. They wait for that run to end, including when HTTP
// recovery finishes it after a disconnect, so leaving the chat still records the creation. A
// transcript loaded from history does not.
const pendingAssistantTableIds = new Map<string, Set<string>>()

const assistantRunEnded = new Set<AgentV2ServerEventType>([
  AgentV2ServerEventType.RUN_COMPLETED,
  AgentV2ServerEventType.RUN_CANCELLED,
  AgentV2ServerEventType.RUN_FAILED,
])

export function clearPendingAssistantTables() {
  pendingAssistantTableIds.clear()
}

function rememberAssistantTable(conversationId: string, tableId: string) {
  const ids = pendingAssistantTableIds.get(conversationId) ?? new Set<string>()
  ids.add(tableId)
  pendingAssistantTableIds.set(conversationId, ids)
}

function emitCompletedAssistantTables(conversationId: string, state: RootState) {
  const ids = pendingAssistantTableIds.get(conversationId)
  if (!ids?.size) return
  pendingAssistantTableIds.delete(conversationId)
  const track = getTrackEventGlobal()
  if (!track) return
  const matterId = selectAgentV2ConversationMatterId(state, { conversationId })
  if (!matterId) return
  for (const tableId of ids) trackAssistantTableCreated(track, tableId, matterId)
}

function dropPendingNewChatMessagesForConversation(conversationId: string) {
  for (const [clientRequestId, pending] of pendingNewChatMessages) {
    if (pending.conversationId === conversationId) pendingNewChatMessages.delete(clientRequestId)
  }
}

function emitAdmittedNewChatMessage(clientRequestId: string, turnId: string) {
  const pending = pendingNewChatMessages.get(clientRequestId)
  if (!pending) return
  pendingNewChatMessages.delete(clientRequestId)
  if (!pending.track) return

  pending.track(AnalyticsEvent.NewChatMessage, { ...pending.properties, turnId })
}

export const agentV2ChatAnalyticsMiddleware = createListenerMiddleware()
const start = agentV2ChatAnalyticsMiddleware.startListening

start({
  actionCreator: AgentV2TranscriptActions.turnSubmitted,
  effect: (action, listenerApi) => {
    const { clientRequestId, conversationId, inputText, matterArtifactSelection, selectedSkillIds, selectedPaxtonSkillNames } = action.payload
    const dictationUsed = matterArtifactSelection ? false : consumeDictationUsed(conversationId)

    const state = listenerApi.getState() as RootState
    const riaResearchEnabled = selectAgentConversationFormResearchEnabled(state, { conversationId })
    const webSearchEnabled = selectAgentConversationFormWebSearchEnabled(state, { conversationId })
    const conversationMatterId = selectAgentV2ConversationMatterId(state, { conversationId })
    const skillProperties = newChatMessageSkillProperties(
      { selectedSkillIds, selectedPaxtonSkillNames },
      state.userSkillLibraryState.paxtonSkills,
      state.userSkillLibraryState.skills,
      inputText
    )

    const track = getTrackEventGlobal()
    pendingNewChatMessages.set(clientRequestId, {
      conversationId,
      track,
      properties: {
        ...(conversationMatterId ? { conversationMatterId } : {}),
        riaResearchEnabled,
        dictationUsed,
        webSearchEnabled,
        ...skillProperties,
        ...(action.payload.tableId ? { tableId: action.payload.tableId } : {}),
      },
    })

    if (!track || !riaResearchEnabled) return

    track(AnalyticsEvent.RiaMessageWithResearchSubmitted, {
      conversationId,
      riaSelectedStatesCount: selectSelectedStateNames(state, { conversationId }).length,
      riaSelectedFederalCount: selectNumberOfCurrentSourcesShownInFederalTab(state, { conversationId }),
      riaAllFederalSourcesSelected: selectAreAllFederalTabSourcesSelected(state, { conversationId }),
      riaIncludeFederalContent: selectHasRelatedFederalContent(state, { conversationId }),
      riaIncludeUnpublishedCaseLaw: selectAgentConversationFormIncludeUnpublishedCases(state, { conversationId }),
      riaDateRange: formatRiaDateRange(selectAgentConversationFormCaselawDateRange(state, { conversationId })),
    })
  },
})

start({
  actionCreator: AgentV2ConversationsActions.runStarted,
  effect: (action) => {
    emitAdmittedNewChatMessage(action.payload.clientRequestId, action.payload.turnId)
  },
})

start({
  actionCreator: AgentV2ConversationsActions.turnAdmissionRecovered,
  effect: (action) => {
    emitAdmittedNewChatMessage(action.payload.clientRequestId, action.payload.turnId)
  },
})

start({
  actionCreator: AgentV2ConversationsActions.runDidNotStart,
  effect: (action) => {
    dropPendingNewChatMessagesForConversation(action.payload.conversationId)
  },
})

start({
  actionCreator: AgentV2ConversationsActions.nullifyData,
  effect: () => {
    pendingNewChatMessages.clear()
    pendingAssistantTableIds.clear()
  },
})

start({
  actionCreator: AgentV2TranscriptActions.serverEventReceived,
  effect: (action, listenerApi) => {
    const { conversationId, event } = action.payload
    if (event.type !== AgentV2ServerEventType.DRAFT_COMPLETED) return

    const track = getTrackEventGlobal()
    if (!track) return

    const state = listenerApi.getState() as RootState
    const { artifact_version: artifactVersion } = event.payload

    track(AnalyticsEvent.ArtifactGenerationCompleted, {
      artifactId: artifactVersion.matter_artifact_id,
      artifactVersionId: artifactVersion.id,
      conversationId,
      matterId: selectAgentV2ConversationMatterId(state, { conversationId }) ?? 'none',
    })
  },
})

start({
  actionCreator: AgentV2TranscriptActions.serverEventReceived,
  effect: (action, listenerApi) => {
    const { conversationId, event } = action.payload
    if (event.type === AgentV2ServerEventType.TABLE_CREATED) {
      rememberAssistantTable(conversationId, event.payload.table_id)
      return
    }
    if (!assistantRunEnded.has(event.type)) return
    emitCompletedAssistantTables(conversationId, listenerApi.getState() as RootState)
  },
})

start({
  actionCreator: AgentV2ConversationsActions.setConversationDetail,
  effect: (action, listenerApi) => {
    const conversationId = action.payload.conversation.id
    const previousPhase = (listenerApi.getOriginalState() as RootState).agentV2ConversationsState.runLifecycleByConversationId[conversationId]?.phase
    // Only the run that was already recovering. A history load, or a detail refresh after the
    // run is idle, has no live `table.created` to settle.
    if (previousPhase !== AgentV2RunPhase.RECOVERING) return
    const state = listenerApi.getState() as RootState
    if (state.agentV2ConversationsState.runLifecycleByConversationId[conversationId]?.phase !== AgentV2RunPhase.IDLE) return
    emitCompletedAssistantTables(conversationId, state)
  },
})
